import { generateMockData } from '../data/mockData';

const getMockData = () => {
  const storedData = localStorage.getItem('mockData');
  if (storedData) {
    return JSON.parse(storedData);
  }
  return generateMockData();
};

export const calcularKPIs = () => {
  const mockData = getMockData();
  const garantiasActivas = mockData.garantias.filter(g => g.estado === 'activa').length;
  const garantiasTotal = mockData.garantias.length;
  const averiasTotal = mockData.averias.length;
  const averiasPendientes = mockData.averias.filter(a => a.estado === 'pendiente' || a.estado === 'en_proceso').length;
  const facturas = mockData.facturas || [];

  const ratioAverias = garantiasActivas > 0
    ? ((averiasTotal / garantiasActivas) * 100).toFixed(2)
    : 0;

  const facturacionTotal = facturas.reduce((sum, f) => sum + (f.importe || 0), 0);
  const facturacionMedia = facturas.length > 0
    ? (facturacionTotal / facturas.length).toFixed(2)
    : 0;

  const facturacionMes = facturas
    .filter(f => {
      const fecha = new Date(f.fechaEmision);
      const now = new Date();
      return fecha.getMonth() === now.getMonth() && fecha.getFullYear() === now.getFullYear();
    })
    .reduce((sum, f) => sum + (f.importe || 0), 0);

  const satisfaccionPromedio = mockData.garantias.length > 0
    ? (mockData.garantias.reduce((sum, g) => sum + (g.satisfaccionCliente || 0), 0) / mockData.garantias.length).toFixed(1)
    : 0;

  return {
    garantiasActivas,
    garantiasTotal,
    averiasTotal,
    averiasPendientes,
    ratioAverias,
    facturacionTotal: facturacionTotal.toFixed(2),
    facturacionMedia,
    facturacionMes: facturacionMes.toFixed(2),
    satisfaccionPromedio,
    crecimiento: (Math.random() * 20 + 5).toFixed(1)
  };
};

export const generarInformeNarrativo = () => {
  const kpis = calcularKPIs();

  const informe = `📊 Análisis del Sistema EUREKA

🔹 Estado de Garantías:
Actualmente hay ${kpis.garantiasActivas} garantías activas de un total de ${kpis.garantiasTotal}. El ratio de averías por garantía es del ${kpis.ratioAverias}%.

🔹 Gestión de Averías:
Se han registrado ${kpis.averiasTotal} averías en total, de las cuales ${kpis.averiasPendientes} están pendientes de resolución.

🔹 Facturación:
La facturación total asciende a ${kpis.facturacionTotal}€, con una media de ${kpis.facturacionMedia}€ por factura. Este mes se ha facturado ${kpis.facturacionMes}€.

🔹 Satisfacción del Cliente:
El nivel de satisfacción promedio es del ${kpis.satisfaccionPromedio}%, lo que indica un buen desempeño general del servicio.`;

  return informe;
};

export const generarRecomendaciones = () => {
  const kpis = calcularKPIs();
  const recomendaciones = [];

  if (kpis.averiasPendientes > 5) {
    recomendaciones.push({
      prioridad: 'alta',
      titulo: 'Averías Pendientes Elevadas',
      descripcion: `Hay ${kpis.averiasPendientes} averías pendientes. Se recomienda asignar más recursos para reducir el tiempo de respuesta.`
    });
  }

  if (parseFloat(kpis.ratioAverias) > 50) {
    recomendaciones.push({
      prioridad: 'media',
      titulo: 'Ratio de Averías Alto',
      descripcion: 'El ratio de averías por garantía es elevado. Considere revisar los procesos de calidad.'
    });
  }

  if (parseFloat(kpis.satisfaccionPromedio) < 70) {
    recomendaciones.push({
      prioridad: 'alta',
      titulo: 'Satisfacción Baja',
      descripcion: 'La satisfacción del cliente está por debajo del objetivo. Se recomienda implementar mejoras en el servicio.'
    });
  }

  if (recomendaciones.length === 0) {
    recomendaciones.push({
      prioridad: 'baja',
      titulo: 'Sistema Operando Correctamente',
      descripcion: 'Todos los indicadores están dentro de los rangos esperados. Continuar con el monitoreo regular.'
    });
  }

  return recomendaciones;
};

export const generarResumenMensual = () => {
  const mockData = getMockData();
  const now = new Date();
  const mesActual = now.getMonth();
  const añoActual = now.getFullYear();

  const garantiasDelMes = mockData.garantias.filter(g => {
    const fecha = new Date(g.fechaAlta);
    return fecha.getMonth() === mesActual && fecha.getFullYear() === añoActual;
  });

  const averiasDelMes = mockData.averias.filter(a => {
    const fecha = new Date(a.fechaReporte);
    return fecha.getMonth() === mesActual && fecha.getFullYear() === añoActual;
  });

  const facturasDelMes = mockData.facturas.filter(f => {
    const fecha = new Date(f.fechaEmision);
    return fecha.getMonth() === mesActual && fecha.getFullYear() === añoActual;
  });

  const facturacionMes = facturasDelMes.reduce((sum, f) => sum + (f.importe || 0), 0);

  return {
    mes: now.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }),
    garantiasNuevas: garantiasDelMes.length,
    averiasRegistradas: averiasDelMes.length,
    facturacionTotal: facturacionMes.toFixed(2),
    facturasEmitidas: facturasDelMes.length
  };
};

export const procesarPreguntaAsistente = (pregunta) => {
  const preguntaLower = pregunta.toLowerCase();
  const kpis = calcularKPIs();

  if (preguntaLower.includes('garantía') || preguntaLower.includes('garantias')) {
    return `📋 Información sobre Garantías:\n\nActualmente tienes ${kpis.garantiasActivas} garantías activas de un total de ${kpis.garantiasTotal}.\n\nLas garantías activas representan contratos vigentes que están siendo monitoreados por el sistema.`;
  }

  if (preguntaLower.includes('avería') || preguntaLower.includes('averias')) {
    return `🔧 Estado de Averías:\n\nHay ${kpis.averiasTotal} averías registradas en total.\n${kpis.averiasPendientes} averías están pendientes de resolución.\n\nEl ratio de averías por garantía es del ${kpis.ratioAverias}%.`;
  }

  if (preguntaLower.includes('factura') || preguntaLower.includes('facturacion')) {
    return `💰 Información de Facturación:\n\nFacturación total: ${kpis.facturacionTotal}€\nFacturación este mes: ${kpis.facturacionMes}€\nMedia por factura: ${kpis.facturacionMedia}€\n\nEl crecimiento estimado es del ${kpis.crecimiento}%.`;
  }

  if (preguntaLower.includes('cliente') || preguntaLower.includes('satisfaccion')) {
    return `😊 Satisfacción del Cliente:\n\nEl nivel de satisfacción promedio es del ${kpis.satisfaccionPromedio}%.\n\nEsto indica un ${parseFloat(kpis.satisfaccionPromedio) >= 80 ? 'excelente' : parseFloat(kpis.satisfaccionPromedio) >= 70 ? 'buen' : 'mejorable'} desempeño del servicio.`;
  }

  if (preguntaLower.includes('resumen') || preguntaLower.includes('estado')) {
    return generarInformeNarrativo();
  }

  if (preguntaLower.includes('recomendacion') || preguntaLower.includes('sugerencia')) {
    const recomendaciones = generarRecomendaciones();
    let respuesta = '💡 Recomendaciones del Sistema:\n\n';
    recomendaciones.forEach((rec, index) => {
      respuesta += `${index + 1}. [${rec.prioridad.toUpperCase()}] ${rec.titulo}\n${rec.descripcion}\n\n`;
    });
    return respuesta;
  }

  return `🤖 Asistente EUREKA:\n\nPuedo ayudarte con información sobre:\n• Garantías activas y su estado\n• Averías registradas y pendientes\n• Facturación y estadísticas financieras\n• Satisfacción del cliente\n• Resumen general del sistema\n• Recomendaciones de mejora\n\n¿Sobre qué te gustaría saber más?`;
};

export const obtenerEstadisticasRapidas = () => {
  const kpis = calcularKPIs();
  return {
    garantiasActivas: kpis.garantiasActivas,
    averiasPendientes: kpis.averiasPendientes,
    facturacionMes: kpis.facturacionMes,
    satisfaccion: kpis.satisfaccionPromedio
  };
};
